import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check, BarChart2, Search, Network, TrendingUp, Settings, Shield } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';

function Reveal({ children, delay = 0, className = '' }) {
  const { ref, isVisible } = useScrollReveal(0.1);
  return (
    <div
      ref={ref}
      className={`${isVisible ? 'reveal-visible' : 'reveal-hidden'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ─── Services Hero ─── */
function ServicesHero() {
  return (
    <section className="bg-brand-navy py-20 lg:py-28" data-testid="services-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl">
          <Reveal>
            <p className="text-brand-orange font-semibold text-sm uppercase tracking-widest mb-4 font-source-sans">
              Our Services
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-lexend font-bold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-tight mb-6">
              Procurement &amp; Supply Chain <span className="text-brand-orange">Services</span>
            </h1>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="font-lexend font-semibold text-xl text-slate-300 mb-6">
              Transform Procurement with Specialist Expertise
            </h2>
          </Reveal>
          <Reveal delay={160}>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed font-source-sans mb-4">
              Accelerate procurement transformation with end-to-end services designed to simplify
              sourcing, streamline supplier collaboration, improve spend visibility, and optimize
              procure-to-pay operations.
            </p>
            <p className="text-slate-300 text-base leading-relaxed font-source-sans mb-4">
              We help enterprises design, implement, integrate, optimize, and support technological
              solutions across the complete Source-to-Pay lifecycle.
            </p>
            <p className="text-slate-300 text-base leading-relaxed font-source-sans">
              Whether you are starting your digital procurement journey, expanding capabilities, or
              optimizing an existing environment, our experts help deliver measurable business outcomes.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ─── Service Card ─── */
function ServiceCard({ icon: Icon, number, title, subheading, items, outcome, alt }) {
  return (
    <Reveal>
      <div
        className={`rounded-2xl border border-brand-border overflow-hidden h-full flex flex-col ${alt ? 'bg-brand-bg' : 'bg-white'}`}
        data-testid={`service-card-${number}`}
      >
        <div className="p-8 flex-1">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-12 h-12 rounded-xl bg-brand-orange-light flex items-center justify-center flex-shrink-0">
              <Icon size={22} className="text-brand-orange" />
            </div>
            <span className="font-jetbrains text-brand-orange font-bold text-sm">0{number}</span>
          </div>
          <h3 className="font-lexend font-bold text-xl text-brand-navy mb-2">{title}</h3>
          <p className="text-slate-500 text-sm font-source-sans mb-5 leading-relaxed">{subheading}</p>
          <ul className="space-y-2">
            {items.map((item) => (
              <li key={item} className="flex items-start gap-2 text-slate-700 text-sm font-source-sans">
                <Check size={14} className="text-brand-orange mt-0.5 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="px-8 py-4 bg-brand-navy border-t border-slate-700">
          <p className="text-slate-300 text-xs font-source-sans leading-relaxed">
            <span className="text-brand-orange font-semibold">Business Outcome: </span>
            {outcome}
          </p>
        </div>
      </div>
    </Reveal>
  );
}

const servicesData = [
  {
    icon: BarChart2,
    number: 1,
    title: 'Advisory & Strategy',
    subheading: 'Build the right procurement roadmap aligned with your business objectives.',
    items: [
      'Procurement maturity assessment',
      'Digital transformation roadmap',
      'Process re-engineering',
      'Spend optimization strategy',
      'Supplier collaboration strategy',
      'Risk and compliance framework',
      'Operating model design',
      'Change readiness assessment',
    ],
    outcome: 'Reduce procurement complexity and create a scalable digital procurement foundation.',
    alt: false,
  },
  {
    icon: Search,
    number: 2,
    title: 'Implementation Services',
    subheading: 'End-to-end deployment of SAP Ariba solutions tailored to your enterprise processes.',
    items: [
      'SAP Ariba Sourcing',
      'SAP Ariba Contracts (CLM)',
      'SAP Ariba Buying & Invoicing',
      'SAP Ariba Supplier Lifecycle & Performance',
      'SAP Ariba Guided Buying',
      'SAP Ariba Catalog Enablement',
      'SAP Ariba Supply Chain Collaboration',
      'SAP Ariba Business Network Integration',
      'SAP Ariba Invoicing',
    ],
    outcome: 'Faster procurement cycles, improved compliance, and increased automation.',
    alt: true,
  },
  {
    icon: Network,
    number: 3,
    title: 'Integration Services',
    subheading: 'Seamless integration between SAP Ariba and enterprise systems.',
    items: [
      'SAP S/4HANA Integration',
      'SAP ECC Integration',
      'Oracle Integration',
      'SAP CIG (Cloud Integration Gateway)',
      'SAP Business Network Connectivity',
      'Third-party ERP integrations',
      'API and middleware integration',
      'Master data synchronization',
      'Supplier data integration',
      'Tax and invoice platform integration',
    ],
    outcome: 'Connected procurement ecosystem with real-time data exchange.',
    alt: false,
  },
  {
    icon: Shield,
    number: 4,
    title: 'Managed Services (AMS)',
    subheading: 'Continuous support and enhancement for stable operations.',
    items: [
      'L1 / L2 / L3 support',
      'Incident & issue resolution',
      'Change requests & enhancements',
      'Release management',
      'Configuration updates',
      'Functional optimization',
      'Performance tuning',
      'Supplier enablement support',
      'SLA-based support model',
      'Hypercare support',
    ],
    outcome: 'Higher system reliability, reduced downtime, and lower support overhead.',
    alt: true,
  },
  {
    icon: Settings,
    number: 5,
    title: 'Migration & Transformation',
    subheading: 'Modernize legacy procurement environments.',
    items: [
      'Legacy procurement system migration',
      'ECC to S/4HANA with Ariba transformation',
      'Re-implementation and redesign',
      'Process harmonization',
      'Multi-country rollout support',
      'Template-driven deployments',
      'Data migration & validation',
    ],
    outcome: 'Standardized global procurement operations with improved visibility.',
    alt: false,
  },
  {
    icon: TrendingUp,
    number: 6,
    title: 'Optimization & Value Realization',
    subheading: 'Maximize ROI from your current investments.',
    items: [
      'Procurement process optimization',
      'Workflow redesign',
      'Approval simplification',
      'Guided buying adoption improvement',
      'Catalog optimization',
      'Compliance improvement',
      'Spend analytics enhancement',
      'Supplier adoption acceleration',
    ],
    outcome: 'Higher adoption, improved savings, and stronger procurement governance.',
    alt: true,
  },
];

/* ─── Services Grid ─── */
function ServicesGrid() {
  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="services-grid">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {servicesData.map((s) => (
            <ServiceCard key={s.title} {...s} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Industry Expertise ─── */
function IndustryExpertise() {
  const industries = [
    'Manufacturing', 'Consumer Goods', 'Retail', 'Energy & Utilities',
    'Life Sciences', 'Automotive', 'Healthcare', 'Banking & Financial Services',
    'Telecom', 'Logistics',
  ];

  return (
    <section className="py-16 lg:py-20 bg-brand-orange" data-testid="industry-expertise">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <Reveal>
            <h2 className="font-lexend font-bold text-2xl lg:text-3xl text-brand-navy tracking-tight mb-3">
              Industry Expertise
            </h2>
          </Reveal>
          <Reveal delay={60}>
            <p className="text-brand-navy text-sm sm:text-base font-source-sans">
              We support procurement transformation across industries:
            </p>
          </Reveal>
        </div>
        <Reveal delay={80}>
          <div className="flex flex-wrap gap-3 justify-center" data-testid="industry-tags">
            {industries.map((ind) => (
              <span
                key={ind}
                className="px-5 py-2.5 bg-white border border-brand-border rounded-full text-sm font-medium text-slate-700 hover:border-brand-orange hover:text-brand-orange hover:bg-brand-orange-light transition-all duration-200 cursor-default font-source-sans"
              >
                {ind}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── Why Choose iMANE ─── */
function WhyChooseImane() {
  const reasons = [
    {
      title: 'Deep SAP Ariba Expertise',
      body: 'Years of experience across sourcing, contracts, supplier collaboration, procure-to-pay, and network integrations.',
    },
    {
      title: 'End-to-End Delivery',
      body: 'From advisory to implementation, integration, AMS, and transformation.',
    },
    {
      title: 'SAP Ecosystem Strength',
      body: 'Expertise across SAP Ariba, SAP S/4HANA, SAP BTP, SAP Business Network, and procurement transformation.',
    },
    {
      title: 'Scalable Global Delivery',
      body: 'Onsite, offshore, and hybrid support models.',
    },
    {
      title: 'Business-First Approach',
      body: 'We focus on measurable value - not just system deployment.',
    },
  ];

  return (
    <section className="py-20 lg:py-28 bg-brand-navy" data-testid="why-choose-imane">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Reveal>
            <h2 className="font-lexend font-bold text-3xl lg:text-4xl text-white tracking-tight mb-4">
              Why Choose iMANE for SAP Ariba?
            </h2>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {reasons.map((r, i) => (
            <Reveal key={r.title} delay={i * 70}>
              <div
                className="bg-slate-800 rounded-xl p-7 border border-slate-700 hover:border-brand-orange transition-colors duration-200 h-full"
                data-testid={`why-choose-card-${i + 1}`}
              >
                <h3 className="font-lexend font-semibold text-white text-lg mb-3">{r.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed font-source-sans">{r.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Engagement Models ─── */
function EngagementModels() {
  const models = [
    'Fixed Scope Implementation',
    'Time & Material',
    'Managed Services (AMS)',
    'Dedicated Support Team',
    'Transformation Program Support',
    'Global Rollout Model',
  ];

  return (
    <section className="py-16 lg:py-20 bg-white border-y border-brand-border" data-testid="engagement-models">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <Reveal>
            <h2 className="font-lexend font-bold text-2xl lg:text-3xl text-brand-navy tracking-tight mb-3">
              Our Engagement Models
            </h2>
          </Reveal>
          <Reveal delay={60}>
            <p className="text-slate-500 text-sm sm:text-base font-source-sans">
              Choose the delivery model that fits your business:
            </p>
          </Reveal>
        </div>
        <Reveal delay={80}>
          <div className="flex flex-wrap gap-3 justify-center">
            {models.map((m) => (
              <span
                key={m}
                className="px-5 py-2.5 bg-brand-navy text-white rounded-full text-sm font-medium font-source-sans border border-slate-700 hover:border-brand-orange transition-colors duration-200 cursor-default"
              >
                {m}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── Business Outcomes ─── */
function BusinessOutcomes() {
  const outcomes = [
    'Faster sourcing cycles',
    'Reduced procurement costs',
    'Improved supplier collaboration',
    'Higher compliance',
    'Better spend visibility',
    'Increased automation',
    'Reduced manual effort',
    'Faster invoice processing',
    'Better procurement governance',
    'Improved ROI from SAP Ariba',
  ];

  return (
    <section className="py-20 lg:py-28 bg-brand-orange" data-testid="business-outcomes">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Reveal>
            <h2 className="font-lexend font-bold text-3xl lg:text-4xl text-brand-navy tracking-tight mb-4">
              Business Outcomes We Deliver
            </h2>
          </Reveal>
        </div>
        <Reveal delay={80}>
          <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4">
            {outcomes.map((o) => (
              <div key={o} className="flex items-center gap-3 bg-white rounded-xl px-6 py-4 border border-brand-border">
                <Check size={16} className="text-brand-orange flex-shrink-0" />
                <span className="text-slate-700 text-sm font-source-sans font-medium">{o}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── Services CTA ─── */
function ServicesCTA() {
  return (
    <section className="py-20 bg-brand-orange" data-testid="services-cta">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Reveal>
          <h2 className="font-lexend font-bold text-3xl lg:text-4xl text-white tracking-tight mb-5">
            Let's Build Smarter Procurement Together
          </h2>
        </Reveal>
        <Reveal delay={80}>
          <p className="text-orange-100 text-base sm:text-lg max-w-3xl mx-auto mb-8 font-source-sans">
            Whether you need a new SAP Ariba implementation, system optimization, integration support,
            or long-term AMS, we help enterprises unlock greater value from procurement technology.
            Talk to our SAP Ariba specialists to accelerate your Source-to-Pay transformation.
          </p>
        </Reveal>
        <Reveal delay={160}>
          <div className="flex flex-col items-center">
            <a
              href="https://wa.me/919637625311"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-white"
              data-testid="services-cta-btn"
            >
              Talk to us <ArrowRight size={16} />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Services() {
  return (
    <div data-testid="services-page">
      <Helmet>
        <title>Procurement &amp; Supply Chain Services | iMANE - Mane Labs LLP</title>
        <meta name="description" content="iMANE offers end-to-end source-to-pay services: SAP Ariba implementation, strategic sourcing, contract management, supplier lifecycle, and S2P managed services across India and APAC." />
        <meta name="keywords" content="SAP Ariba implementation India, procurement consulting services, source to pay services India, iMANE services, mane labs services, strategic sourcing India" />
        <link rel="canonical" href="https://www.imane.in/services" />
        <meta property="og:title" content="Procurement &amp; Supply Chain Services | iMANE - Mane Labs LLP" />
        <meta property="og:description" content="End-to-end source-to-pay services: SAP Ariba implementation, strategic sourcing, contract management, supplier lifecycle management." />
        <meta property="og:url" content="https://www.imane.in/services" />
      </Helmet>
      <ServicesHero />
      <ServicesGrid />
      <IndustryExpertise />
      <WhyChooseImane />
      <EngagementModels />
      <BusinessOutcomes />
      <ServicesCTA />
    </div>
  );
}
